import { CountUp } from 'countup.js';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import React from 'react';

type CountupProps = {
	value: number;
	onlyTickFromZeroOnceKey?: string;
	className?: string;
	formattingFn?: (value: number) => string;
	decimals?: number;
};

const useCountupRef = ({
	value,
	onlyTickFromZeroOnceKey,
	formattingFn,
	...props
}: CountupProps) => {
	const setDriftStore = useDriftStore((s) => {
		return s.set;
	});

	const countUpOnceStore = useDriftStore((s) => {
		return s.countUpOnce;
	});

	const countupRef = useRef(null);
	const countupInstanceRef = useRef<CountUp>();

	const shouldSkipFirstTickFromZero =
		!!countUpOnceStore?.[onlyTickFromZeroOnceKey];

	useEffect(() => {
		initCountUp();
	}, [countupRef.current]);

	useEffect(() => {
		if (!countupInstanceRef.current) {
			return;
		}

		countupInstanceRef.current.update(value);
	}, [value]);

	async function initCountUp() {
		if (countupRef.current) {
			countupInstanceRef.current = new CountUp(countupRef.current, value, {
				startVal: shouldSkipFirstTickFromZero ? value : 0,
				onCompleteCallback: handleStartComplete,
				formattingFn,
				decimalPlaces: props.decimals,
			});
			countupInstanceRef.current.start();
		}
	}

	const setDoneFirstTickFromZero = () => {
		if (onlyTickFromZeroOnceKey) {
			setDriftStore((s) => {
				s.countUpOnce[onlyTickFromZeroOnceKey] = true;
			});
		}
	};

	const handleStartComplete = () => {
		if (onlyTickFromZeroOnceKey) {
			setDoneFirstTickFromZero();
		}
	};

	return countupRef;
};

const Countup = ({
	value,
	onlyTickFromZeroOnceKey,
	className,
	formattingFn,
	decimals,
}: CountupProps) => {
	const countupRef = useCountupRef({
		value,
		onlyTickFromZeroOnceKey,
		formattingFn,
		decimals,
	});

	return <span className={twMerge(className)} ref={countupRef} />;
};

export default React.memo(Countup);
